import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Music, Download, ExternalLink, Shield, Star } from 'lucide-react';
import SEO from '../components/SEO';
import LiveTicker from '../components/LiveTicker';
import TodaysRoomSection from '../components/TodaysRoomSection';
import StoreSection from '../components/StoreSection';
import SponsorDisplay from '../components/SponsorDisplay';
import PollSection from '../components/PollSection';
import VIPMarquee from '../components/VIPMarquee';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetch(`${API}/api/site-settings`).then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  const appStoreUrl = settings.listlive_appstore_url || 'https://apps.apple.com/us/app/listlive-llc/id6756198977';
  const playStoreUrl = settings.listlive_playstore_url || 'https://play.google.com/store/apps/details?id=com.chopsuey.listlive&pcampaignid=web_share';
  const listliveImage = settings.listlive_image_url || '/listlive-promo.png';

  return (
    <div className="min-h-screen bg-black" data-testid="homepage">
      <SEO title="Mediterranean Club" description="Your second-screen companion for live Clubhouse conversations." url="/" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#E91E8D]/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#6366F1]/5 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <img src="/med-logo.png" alt="Mediterranean Club" className="w-16 h-16 mx-auto mb-6 drop-shadow-[0_0_30px_rgba(233,30,141,0.3)]" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight" data-testid="hero-title">
              Mediterranean <span className="text-gradient">Club</span>
            </h1>
            <p className="text-white/40 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Your second-screen companion for live Clubhouse conversations. Say what's on your mind or request a song — completely anonymous.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VIP Events Marquee */}
      <VIPMarquee />

      {/* Feature Cards */}
      <section className="px-4 pb-20">
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Live Chat Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Link to="/chat" className="block group" data-testid="feature-chat">
              <div className="glass-card p-8 text-center h-full transition-all duration-300 hover:border-[#E91E8D]/30 hover:shadow-[0_0_40px_rgba(233,30,141,0.1)]">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#E91E8D]/10 border border-[#E91E8D]/20 flex items-center justify-center group-hover:bg-[#E91E8D]/20 transition-colors">
                  <MessageCircle size={28} className="text-[#E91E8D]" />
                </div>
                <h2 className="font-display text-xl font-bold text-white mb-2">Live Chat</h2>
                <p className="text-white/40 text-sm leading-relaxed mb-5">
                  Join the conversation in real time while listening live on Clubhouse. Chat, share GIFs, upload images, and react to the conversation as it happens.
                </p>
                <span className="inline-flex items-center gap-2 text-[#E91E8D] text-xs font-bold tracking-wider uppercase group-hover:gap-3 transition-all">
                  Enter Chat <ExternalLink size={12} />
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Music Request Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/request-song" className="block group" data-testid="feature-music">
              <div className="glass-card p-8 text-center h-full transition-all duration-300 hover:border-[#8B5CF6]/30 hover:shadow-[0_0_40px_rgba(139,92,246,0.1)]">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 flex items-center justify-center group-hover:bg-[#8B5CF6]/20 transition-colors">
                  <Music size={28} className="text-[#8B5CF6]" />
                </div>
                <h2 className="font-display text-xl font-bold text-white mb-2">Music Request</h2>
                <p className="text-white/40 text-sm leading-relaxed mb-5">
                  Request a song for the room. Tell us the track and artist — we'll queue it up.
                </p>
                <span className="inline-flex items-center gap-2 text-[#8B5CF6] text-xs font-bold tracking-wider uppercase group-hover:gap-3 transition-all">
                  Request a Song <ExternalLink size={12} />
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Spotlight Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="sm:col-span-2"
          >
            <Link to="/spotlight" className="block group" data-testid="feature-spotlight">
              <div className="glass-card p-8 text-center h-full transition-all duration-300 hover:border-[#E91E8D]/30 hover:shadow-[0_0_40px_rgba(233,30,141,0.1)]">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#E91E8D]/10 border border-[#E91E8D]/20 flex items-center justify-center group-hover:bg-[#E91E8D]/20 transition-colors">
                  <Star size={28} className="text-[#E91E8D]" />
                </div>
                <h2 className="font-display text-xl font-bold text-white mb-2">Spotlight the Room</h2>
                <p className="text-white/40 text-sm leading-relaxed mb-2">
                  Put your message, shout-out, announcement, social handle, or business in front of the room.
                </p>
                <p className="text-white/20 text-xs mb-5">Starting at $3</p>
                <span className="inline-flex items-center gap-2 text-[#E91E8D] text-xs font-bold tracking-wider uppercase group-hover:gap-3 transition-all">
                  Buy a Spotlight <ExternalLink size={12} />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Privacy Note */}
      <section className="px-4 pb-16">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-white/20 text-[11px]">
            <Shield size={12} />
            <span>All messages are anonymous. No names, emails, or identifying info are shared publicly. Messages are reviewed before appearing.</span>
          </div>
        </div>
      </section>

      {/* ListLive Section */}
      <section className="px-4 pb-20" data-testid="listlive-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card overflow-hidden"
          >
            <div className="p-8 sm:p-10 text-center">
              {listliveImage && (
                <img
                  src={listliveImage}
                  alt="ListLive - Download the App"
                  className="w-full max-w-lg mx-auto mb-8 rounded-xl"
                  data-testid="listlive-image"
                />
              )}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 mb-5">
                <Download size={12} className="text-[#6366F1]" />
                <span className="text-[#6366F1] text-[10px] font-bold tracking-widest uppercase">Download the App</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">Get ListLive</h2>
              <p className="text-white/40 text-sm max-w-md mx-auto mb-8 leading-relaxed">
                Take the Mediterranean experience with you. Download ListLive and stay connected to the community.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all hover:bg-white/10 w-full sm:w-auto justify-center"
                  data-testid="listlive-appstore"
                >
                  <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  <div className="text-left">
                    <span className="text-white/40 text-[9px] block leading-none">Download on the</span>
                    <span className="text-white text-sm font-semibold">App Store</span>
                  </div>
                </a>
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all hover:bg-white/10 w-full sm:w-auto justify-center"
                  data-testid="listlive-playstore"
                >
                  <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6"><path d="M3 20.5V3.5c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.56.69.56 1.19s-.22.92-.56 1.19l-2.29 1.32-2.5-2.5 2.5-2.5 2.29 1.3M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/></svg>
                  <div className="text-left">
                    <span className="text-white/40 text-[9px] block leading-none">Get it on</span>
                    <span className="text-white text-sm font-semibold">Google Play</span>
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Today's Room */}
      <TodaysRoomSection />

      {/* Sponsor of the Day */}
      <SponsorDisplay />

      {/* Live Polls */}
      <PollSection />

      {/* Store */}
      <StoreSection />

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 pb-20 px-4" data-testid="footer">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/med-logo.png" alt="M" className="w-5 h-5 opacity-40" />
            <span className="text-white/20 text-xs">&copy; {new Date().getFullYear()} Mediterranean Club</span>
          </div>
          <div className="text-white/15 text-[10px]">All messages are moderated</div>
        </div>
      </footer>

      {/* Live Tickers */}
      <LiveTicker />
    </div>
  );
};

export default HomePage;
